<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(false);
const errorMsg = ref('');
const data = ref(null);
const days = ref(28);
const site = ref('modax');
const speaking = ref(false);

const SITES = [
  { key: 'modax', label: 'ModaXLarge', domain: 'modaxlarge.com' },
  { key: 'koza', label: 'Koza Butik', domain: 'kozabutik.com' },
  { key: 'adrom', label: 'Adrom (B2B)', domain: 'adromfashion.com' },
];
const siteLabel = key => SITES.find(s => s.key === key)?.label || key;
const siteDomain = computed(
  () => SITES.find(s => s.key === site.value)?.domain || ''
);

// "?" yardım pencereleri — Google panelindeki kalıbın aynısı.
const helpKey = ref(null);
const HELP = {
  yorum: {
    title: 'Otomatik Yorum',
    text: 'Bu kutu, aşağıdaki tüm rakamları senin yerine okuyup sade Türkçeyle özetler: ziyaret nereye gidiyor, satış oranı ne durumda, telefon mu bilgisayar mı satıyor. Rakamlara hiç bakmadan sadece bu kutuyu okumak (veya dinlemek) günlük takip için yeterlidir.',
  },
  karne: {
    title: 'Üç Sitenin Karnesi',
    text: 'Üç sitemizin aynı dönemdeki ziyaret, satış ve cirosu yan yana. Ok işaretleri önceki dönemle karşılaştırır: ▲ yükseliş, ▼ düşüş demektir. Bir siteye tıklayınca aşağıdaki tüm detay o siteye geçer.',
  },
  kpi: {
    title: 'Ana Göstergeler',
    text: 'Sitenin nabzı: kaç ziyaret geldi, kaç kişi geldi, ne kadar kaldılar, kaçı sepete ekledi, kaçı satın aldı. Her kutunun altındaki ok, aynı uzunluktaki ÖNCEKİ dönemle kıyaslar — yani "28 gün" seçiliyken önceki 28 günle. Yüzdelerden çok yönü izle: yeşil ▲ iyi, kırmızı ▼ dikkat.',
  },
  cihaz: {
    title: 'Cihaz Dağılımı',
    text: 'Ziyaretçiler siteye telefondan mı bilgisayardan mı giriyor, hangisi daha çok satın alıyor? Giyim alışverişi ağırlıkla telefondan yapılır; telefon dönüşümü bilgisayarın çok altındaysa mobil sayfada bir sorun (yavaşlık, zor ödeme adımı) olabilir demektir.',
  },
  ziyaretci: {
    title: 'İlk Kez Gelen ve Geri Gelen',
    text: 'İlk kez gelen = siteyi yeni keşfeden. Geri gelen = daha önce gelmiş, tekrar gelmiş. Geri gelenler genelde kat kat daha çok satın alır — bu yüzden ziyaretçiyi geri getiren her şey (hatırlatma reklamı, WhatsApp mesajı) değerlidir. İki grubun dönüşüm farkı bu değeri gösterir.',
  },
  saat: {
    title: 'Saatlik Nabız',
    text: 'Günün hangi saatlerinde site canlı? Yoğun saatler; Instagram paylaşımı, reklam ve kampanya duyurusu için en doğru zamandır. Çubuğun üstüne gelince o saatin ziyaret ve satışını görürsün. Saatler Türkiye saatidir.',
  },
  sayfa: {
    title: 'Açılış Sayfaları',
    text: 'Ziyaretçiler siteye hangi kapıdan giriyor? Buradaki sayfalar sitenin vitrinidir: en çok girilen sayfanın etkileşimi düşükse ziyaretçi kapıdan bakıp çıkıyor demektir — o sayfayı güçlendirmek (görsel, fiyat, ürün dizilimi) en hızlı kazanımdır.',
  },
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    data.value = await fetchMarketplaceJson(
      accountId,
      `google_stack/analytics?days=${days.value}&site=${site.value}`
    );
  } catch (e) {
    errorMsg.value =
      'Google Analytics verisi yüklenemedi — bağlantıyı kontrol edelim.';
  } finally {
    loading.value = false;
  }
};

const switchSite = s => {
  if (site.value === s) return;
  site.value = s;
  data.value = null;
  load();
};
const switchDays = d => {
  days.value = d;
  data.value = null;
  load();
};

// ---- Sesli okuma (tr-TR): yorum kutusunu okur ----
const speak = () => {
  if (speaking.value) {
    window.speechSynthesis.cancel();
    speaking.value = false;
    return;
  }
  const text = `${siteLabel(site.value)} analiz raporu. ${(data.value?.comments || []).join(' ')}`;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = 'tr-TR';
  u.rate = 0.95;
  u.onend = () => {
    speaking.value = false;
  };
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
  speaking.value = true;
};
onBeforeUnmount(() => window.speechSynthesis?.cancel());

// ---- Biçimleyiciler ----
const nf = n => (n || 0).toLocaleString('tr-TR');
const durFmt = s => `${((s || 0) / 60).toFixed(1).replace('.', ',')} dk`;

// Önceki döneme göre değişim: {dir: 'up'|'down'|'flat', pct}
const delta = (cur, prev) => {
  if (!prev || prev < 1) return null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (Math.abs(pct) < 3) return { dir: 'flat', pct: 0 };
  return { dir: pct > 0 ? 'up' : 'down', pct: Math.abs(pct) };
};

// Ana gösterge kutuları — cur/prev çiftinden delta hesaplanır.
const tiles = computed(() => {
  if (!data.value?.kpis) return [];
  const c = data.value.kpis.current;
  const p = data.value.kpis.previous;
  const conv = c.sessions > 0 ? (c.ecommercePurchases / c.sessions) * 100 : 0;
  const convPrev =
    p.sessions > 0 ? (p.ecommercePurchases / p.sessions) * 100 : 0;
  const aov =
    c.ecommercePurchases > 0 ? c.purchaseRevenue / c.ecommercePurchases : 0;
  const aovPrev =
    p.ecommercePurchases > 0 ? p.purchaseRevenue / p.ecommercePurchases : 0;
  return [
    {
      icon: '👣',
      label: 'Ziyaret',
      value: nf(Math.round(c.sessions)),
      d: delta(c.sessions, p.sessions),
    },
    {
      icon: '🧑',
      label: 'Ziyaretçi',
      value: nf(Math.round(c.activeUsers)),
      d: delta(c.activeUsers, p.activeUsers),
    },
    {
      icon: '🆕',
      label: 'Yeni Ziyaretçi',
      value: nf(Math.round(c.newUsers)),
      d: delta(c.newUsers, p.newUsers),
    },
    {
      icon: '📄',
      label: 'Sayfa Görüntüleme',
      value: nf(Math.round(c.screenPageViews)),
      d: delta(c.screenPageViews, p.screenPageViews),
    },
    {
      icon: '🤝',
      label: 'Etkileşim Oranı',
      value: `%${Math.round(c.engagementRate * 100)}`,
      d: delta(c.engagementRate, p.engagementRate),
    },
    {
      icon: '⏱',
      label: 'Ort. Ziyaret Süresi',
      value: durFmt(c.averageSessionDuration),
      d: delta(c.averageSessionDuration, p.averageSessionDuration),
    },
    {
      icon: '🛒',
      label: 'Sepete Ekleme',
      value: nf(Math.round(c.addToCarts)),
      d: delta(c.addToCarts, p.addToCarts),
    },
    {
      icon: '✅',
      label: 'Satış',
      value: nf(Math.round(c.ecommercePurchases)),
      d: delta(c.ecommercePurchases, p.ecommercePurchases),
      guarded: true,
    },
    {
      icon: '💰',
      label: 'Ciro',
      value: `${nf(Math.round(c.purchaseRevenue))} TL`,
      d: delta(c.purchaseRevenue, p.purchaseRevenue),
      guarded: true,
    },
    {
      icon: '🎯',
      label: 'Dönüşüm',
      value: `%${conv.toFixed(2).replace('.', ',')}`,
      d: delta(conv, convPrev),
      guarded: true,
    },
    {
      icon: '🧺',
      label: 'Ortalama Sepet',
      value: `${nf(Math.round(aov))} TL`,
      d: delta(aov, aovPrev),
      guarded: true,
    },
  ];
});

const DEVICE_TR = {
  mobile: '📱 Telefon',
  desktop: '💻 Bilgisayar',
  tablet: '📟 Tablet',
};
const VISITOR_TR = { new: '🆕 İlk kez gelen', returning: '🔁 Geri gelen' };

const deviceRows = computed(() =>
  (data.value?.devices || [])
    .filter(d => DEVICE_TR[d.key])
    .sort((a, b) => b.sessions - a.sessions)
);
const maxDeviceSessions = computed(() =>
  Math.max(...deviceRows.value.map(d => d.sessions), 1)
);

const visitorRows = computed(() =>
  (data.value?.visitors || []).filter(v => VISITOR_TR[v.key])
);

const hourRows = computed(() => data.value?.hours || []);
const maxHourSessions = computed(() =>
  Math.max(...hourRows.value.map(h => h.sessions), 1)
);

const convOf = r =>
  r.sessions > 0
    ? ((r.purchases / r.sessions) * 100).toFixed(2).replace('.', ',')
    : '—';

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-6xl w-full mx-auto px-6 py-8">
      <!-- Başlık + site & dönem seçimi -->
      <div class="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div
            class="text-xs font-semibold uppercase tracking-widest text-n-slate-10"
          >
            {{ siteDomain }} — Google Analytics
          </div>
          <h1 class="text-3xl font-semibold text-n-slate-12 mt-1">
            📊 Analiz Merkezi
          </h1>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-for="s in SITES"
            :key="s.key"
            class="rounded-full px-4 py-2 text-sm font-semibold border"
            :class="
              site === s.key
                ? 'bg-woot-600 border-woot-600 text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="switchSite(s.key)"
          >
            {{ s.label }}
          </button>
          <span class="w-px h-6 bg-n-weak mx-1" />
          <button
            v-for="d in [7, 28, 90]"
            :key="d"
            class="rounded-full border px-3 py-2 text-sm"
            :class="
              days === d
                ? 'bg-n-solid-3 border-n-strong font-bold text-n-slate-12'
                : 'border-n-weak text-n-slate-11'
            "
            @click="switchDays(d)"
          >
            {{ d }} gün
          </button>
        </div>
      </div>

      <div
        v-if="errorMsg"
        class="rounded-lg bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-4 py-3 mb-4 text-sm"
      >
        {{ errorMsg }}
      </div>
      <div v-if="loading" class="text-n-slate-11 py-10 text-center text-lg">
        Google Analytics'ten veri çekiliyor… (üç sitenin karnesi hazırlanıyor,
        birkaç saniye sürebilir)
      </div>

      <template v-if="data && !loading">
        <!-- ============ OTOMATİK YORUM ============ -->
        <div class="rounded-2xl border-2 border-woot-500 bg-n-solid-1 p-6 mb-8">
          <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div class="flex items-center gap-2">
              <h2 class="text-xl font-semibold text-n-slate-12">
                🧠 Bu Rakamlar Ne Anlatıyor?
              </h2>
              <button
                class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
                @click="helpKey = 'yorum'"
              >
                ?
              </button>
            </div>
            <button
              class="rounded-full px-4 py-2 text-sm font-semibold border"
              :class="
                speaking
                  ? 'bg-ruby-700 border-ruby-700 text-white'
                  : 'bg-n-solid-3 border-n-strong text-n-slate-12 hover:bg-n-solid-2'
              "
              @click="speak"
            >
              {{ speaking ? '⏹ Durdur' : '🔊 Sesli Oku' }}
            </button>
          </div>
          <ul class="space-y-3">
            <li
              v-for="(c, i) in data.comments"
              :key="i"
              class="text-lg leading-relaxed text-n-slate-12 flex gap-3"
            >
              <span class="text-woot-500 font-bold shrink-0">•</span>
              <span>{{ c }}</span>
            </li>
          </ul>
        </div>

        <!-- ============ ÜÇ SİTE KARNESİ ============ -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            🏁 Üç Sitenin Karnesi ({{ data.days }} gün)
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'karne'"
          >
            ?
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <button
            v-for="s in data.summary"
            :key="s.site"
            class="text-left rounded-2xl border-2 p-5 transition-all"
            :class="
              site === s.site
                ? 'border-woot-500 bg-n-solid-2'
                : 'border-n-weak bg-n-solid-1 hover:border-n-strong'
            "
            @click="switchSite(s.site)"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="text-lg font-bold text-n-slate-12">{{
                siteLabel(s.site)
              }}</span>
              <span
                v-if="site === s.site"
                class="text-xs font-semibold text-woot-600 dark:text-woot-400"
                >İNCELENEN ▾</span>
            </div>
            <template v-if="!s.error">
              <div class="flex items-baseline gap-2">
                <span class="text-2xl font-bold tabular-nums text-n-slate-12">{{
                  nf(s.sessions)
                }}</span>
                <span class="text-sm text-n-slate-11">ziyaret</span>
                <span
                  v-if="delta(s.sessions, s.sessions_prev)?.dir === 'up'"
                  class="text-sm font-bold text-green-700 dark:text-green-400"
                  >▲%{{ delta(s.sessions, s.sessions_prev).pct }}</span>
                <span
                  v-else-if="delta(s.sessions, s.sessions_prev)?.dir === 'down'"
                  class="text-sm font-bold text-ruby-700 dark:text-ruby-400"
                  >▼%{{ delta(s.sessions, s.sessions_prev).pct }}</span>
              </div>
              <div
                v-if="s.measurement_ok"
                class="flex items-baseline gap-2 mt-1"
              >
                <span class="text-2xl font-bold tabular-nums text-n-slate-12">{{ nf(s.revenue) }} TL</span>
                <span class="text-sm text-n-slate-11">· {{ s.purchases }} satış</span>
                <span
                  v-if="delta(s.revenue, s.revenue_prev)?.dir === 'up'"
                  class="text-sm font-bold text-green-700 dark:text-green-400"
                  >▲%{{ delta(s.revenue, s.revenue_prev).pct }}</span>
                <span
                  v-else-if="delta(s.revenue, s.revenue_prev)?.dir === 'down'"
                  class="text-sm font-bold text-ruby-700 dark:text-ruby-400"
                  >▼%{{ delta(s.revenue, s.revenue_prev).pct }}</span>
              </div>
              <div
                v-else
                class="mt-1 text-sm font-semibold text-yellow-700 dark:text-yellow-400"
              >
                ⚠️ Satış ölçümü henüz akmıyor
              </div>
            </template>
            <div v-else class="text-sm text-n-slate-10">Veri alınamadı</div>
          </button>
        </div>

        <!-- ============ ANA GÖSTERGELER ============ -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            🎛 Ana Göstergeler — {{ siteLabel(site) }}
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'kpi'"
          >
            ?
          </button>
        </div>
        <div
          v-if="site === 'koza'"
          class="rounded-lg bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-4 py-3 mb-4 text-sm"
        >
          ⚠️ Koza'da satış/ciro/dönüşüm kutuları ölçüm arızası nedeniyle 0
          görünür — satış yok demek değildir. Ziyaret ve davranış kutuları
          gerçektir.
        </div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div
            v-for="tile in tiles"
            :key="tile.label"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-4"
            :class="{ 'opacity-50': tile.guarded && site === 'koza' }"
          >
            <div class="text-sm text-n-slate-11 mb-1">
              {{ tile.icon }} {{ tile.label }}
            </div>
            <div class="text-2xl font-bold tabular-nums text-n-slate-12">
              {{ tile.value }}
            </div>
            <div v-if="tile.d" class="mt-1 text-sm font-semibold">
              <span
                v-if="tile.d.dir === 'up'"
                class="text-green-700 dark:text-green-400"
                >▲ %{{ tile.d.pct }} arttı</span>
              <span
                v-else-if="tile.d.dir === 'down'"
                class="text-ruby-700 dark:text-ruby-400"
                >▼ %{{ tile.d.pct }} azaldı</span>
              <span v-else class="text-n-slate-10">≈ aynı seviyede</span>
            </div>
          </div>
        </div>

        <!-- ============ CİHAZ DAĞILIMI ============ -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            📱 Cihaz Dağılımı
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'cihaz'"
          >
            ?
          </button>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 mb-8">
          <div v-for="d in deviceRows" :key="d.key" class="mb-4 last:mb-0">
            <div
              class="flex flex-wrap items-baseline justify-between gap-2 mb-1"
            >
              <span class="font-semibold text-n-slate-12">{{
                DEVICE_TR[d.key]
              }}</span>
              <span class="text-sm text-n-slate-11 tabular-nums">
                {{ nf(d.sessions) }} ziyaret · etkileşim %{{ d.engagement
                }}<template v-if="site !== 'koza'">
                  · {{ d.purchases }} satış · dönüşüm %{{ convOf(d) }}</template>
              </span>
            </div>
            <div class="h-5 rounded bg-n-solid-2 overflow-hidden">
              <div
                class="h-full rounded bg-woot-500"
                :style="{
                  width: `${Math.max((d.sessions / maxDeviceSessions) * 100, 1)}%`,
                }"
              />
            </div>
          </div>
        </div>

        <!-- ============ İLK KEZ / GERİ GELEN ============ -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            🔁 İlk Kez Gelen ve Geri Gelen
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'ziyaretci'"
          >
            ?
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div
            v-for="v in visitorRows"
            :key="v.key"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-5"
          >
            <div class="text-lg font-bold text-n-slate-12 mb-2">
              {{ VISITOR_TR[v.key] }}
            </div>
            <div class="grid grid-cols-3 gap-2 text-center">
              <div>
                <div class="text-xl font-bold tabular-nums text-n-slate-12">
                  {{ nf(v.sessions) }}
                </div>
                <div class="text-xs text-n-slate-11">ziyaret</div>
              </div>
              <div>
                <div class="text-xl font-bold tabular-nums text-n-slate-12">
                  %{{ v.engagement }}
                </div>
                <div class="text-xs text-n-slate-11">etkileşim</div>
              </div>
              <div v-if="site !== 'koza'">
                <div class="text-xl font-bold tabular-nums text-n-slate-12">
                  %{{ convOf(v) }}
                </div>
                <div class="text-xs text-n-slate-11">dönüşüm</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ SAATLİK NABIZ ============ -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            🕐 Saatlik Nabız
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'saat'"
          >
            ?
          </button>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 mb-8">
          <div class="flex items-end gap-[3px] h-32">
            <div
              v-for="h in hourRows"
              :key="h.key"
              class="flex-1 flex flex-col items-center group relative"
            >
              <div
                class="w-full rounded-t bg-woot-500 group-hover:bg-woot-600 transition-all"
                :style="{
                  height: `${Math.max((h.sessions / maxHourSessions) * 100, 2)}%`,
                }"
              />
              <div
                class="hidden group-hover:block absolute -top-12 bg-n-solid-3 border border-n-weak rounded-lg px-2 py-1 text-xs whitespace-nowrap z-10"
              >
                {{ h.key }}:00 — {{ nf(h.sessions) }} ziyaret<template
                  v-if="site !== 'koza'"
                >
                  · {{ h.purchases }} satış
                </template>
              </div>
            </div>
          </div>
          <div class="flex justify-between text-xs text-n-slate-10 mt-2">
            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
          </div>
          <div class="text-xs text-n-slate-10 mt-1 text-center">
            ziyaret/saat (Türkiye saati) — çubuğun üstüne gel, detayı gör
          </div>
        </div>

        <!-- ============ AÇILIŞ SAYFALARI ============ -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            🚪 Açılış Sayfaları (siteye giriş kapıları)
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'sayfa'"
          >
            ?
          </button>
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-solid-2 text-n-slate-10 text-left">
                  <th class="px-4 py-2">Sayfa</th>
                  <th class="px-4 py-2 text-right">Ziyaret</th>
                  <th class="px-4 py-2 text-right">Etkileşim %</th>
                  <th v-if="site !== 'koza'" class="px-4 py-2 text-right">
                    Satış
                  </th>
                  <th v-if="site !== 'koza'" class="px-4 py-2 text-right">
                    Ciro (TL)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="p in data.pages"
                  :key="p.key"
                  class="border-t border-n-weak"
                >
                  <td class="px-4 py-2 text-n-slate-12 max-w-md truncate">
                    {{ p.key === '/' ? '/ (ana sayfa)' : p.key }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums font-semibold">
                    {{ nf(p.sessions) }}
                  </td>
                  <td
                    class="px-4 py-2 text-right tabular-nums"
                    :class="
                      p.sessions > 100 && p.engagement < 40
                        ? 'text-ruby-700 dark:text-ruby-400 font-bold'
                        : ''
                    "
                  >
                    {{ p.engagement }}
                  </td>
                  <td
                    v-if="site !== 'koza'"
                    class="px-4 py-2 text-right tabular-nums"
                  >
                    {{ p.purchases }}
                  </td>
                  <td
                    v-if="site !== 'koza'"
                    class="px-4 py-2 text-right tabular-nums font-bold"
                  >
                    {{ nf(p.revenue) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>

    <!-- "?" AÇIKLAMA PENCERESİ -->
    <div
      v-if="helpKey"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6"
      @click.self="helpKey = null"
    >
      <div
        class="max-w-xl w-full rounded-2xl bg-n-solid-1 border border-n-weak p-6 shadow-xl"
      >
        <div class="flex items-start justify-between gap-4 mb-3">
          <h3 class="text-2xl font-semibold text-n-slate-12">
            💡 {{ HELP[helpKey].title }}
          </h3>
          <button
            class="rounded-full border border-n-weak px-3 py-1 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-3"
            @click="helpKey = null"
          >
            Kapat ✕
          </button>
        </div>
        <p class="text-lg leading-relaxed text-n-slate-12">
          {{ HELP[helpKey].text }}
        </p>
      </div>
    </div>
  </div>
</template>
